import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Clock, CheckCircle, XCircle, Calendar } from 'lucide-react';
import { panelRequestAPI } from '../../services/api';
import { PanelRequest } from '../../types';

const MyPanelRequests: React.FC = () => {
  const [requests, setRequests] = useState<PanelRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await panelRequestAPI.getUserRequests();
      setRequests(response);
    } catch (err) {
      setError('Failed to fetch panel requests');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">My Panel Requests</h2>
        
        {requests.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No panel requests found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      {getStatusIcon(request.status)}
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Panel Details</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div><strong>Brand:</strong> {request.panelData.panelData?.brand}</div>
                          <div><strong>Model:</strong> {request.panelData.panelData?.model}</div>
                          <div><strong>Wattage:</strong> {request.panelData.panelData?.wattage}W</div>
                          <div><strong>Efficiency:</strong> {request.panelData.panelData?.efficiency}%</div>
                          <div><strong>Technology:</strong> {request.panelData.panelData?.technology}</div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Physical Specs</h4>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div><strong>Dimensions:</strong> {request.panelData.panelData?.dimensions}</div>
                          <div><strong>Weight:</strong> {request.panelData.panelData?.weight}kg</div>
                          <div><strong>Frame Color:</strong> {request.panelData.panelData?.frameColor}</div>
                          <div><strong>Warranty:</strong> {request.panelData.panelData?.warranty} years</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>Requested on {new Date(request.created_at).toLocaleDateString()}</span>
                    </div>
                    
                    {request.adminNotes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <h5 className="font-medium text-gray-900 mb-1">Admin Notes:</h5>
                        <p className="text-sm text-gray-700">{request.adminNotes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { MyPanelRequests };
